import { z } from "zod";
import { Id, Timestamp } from "./common.js";

/** Calendar date in the user's timezone, YYYY-MM-DD. */
export const DueDate = z.iso.date();
/** Local time of day, HH:MM (24h). */
export const DueTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Expected HH:MM");

const Title = z.string().trim().min(1).max(200);
const Description = z.string().max(5000);

export const Todo = z.object({
  id: Id,
  title: z.string(),
  description: z.string().nullable(),
  important: z.boolean(),
  done: z.boolean(),
  dueDate: DueDate.nullable(),
  dueTime: DueTime.nullable(),
  completedAt: Timestamp.nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Todo = z.infer<typeof Todo>;

const dueTimeNeedsDate = (todo: { dueDate?: string | null; dueTime?: string | null }) =>
  !todo.dueTime || !!todo.dueDate;
const dueTimeIssue = { message: "A due time needs a due date", path: ["dueTime"] };

export const CreateTodo = z
  .object({
    title: Title,
    description: Description.nullish(),
    important: z.boolean().optional(),
    dueDate: DueDate.nullish(),
    dueTime: DueTime.nullish(),
  })
  .refine(dueTimeNeedsDate, dueTimeIssue);
export type CreateTodo = z.infer<typeof CreateTodo>;

/** Partial update. Setting dueDate to null also clears dueTime. */
export const UpdateTodo = z
  .object({
    title: Title,
    description: Description.nullable(),
    important: z.boolean(),
    done: z.boolean(),
    dueDate: DueDate.nullable(),
    dueTime: DueTime.nullable(),
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: "Nothing to update" });
export type UpdateTodo = z.infer<typeof UpdateTodo>;

export const TodoParams = z.object({ id: Id });

export const TodoList = z.object({ todos: z.array(Todo) });
export type TodoList = z.infer<typeof TodoList>;
